<template>
  <v-card>
    <v-card-title>
      <span class="text-h5">Logs de Webhooks</span>
      <v-spacer></v-spacer>

      <!-- Filtro por webhook -->
      <v-select
        v-if="!webhookId"
        v-model="selectedWebhookFilter"
        :items="webhookOptions"
        item-text="name"
        item-value="_id"
        label="Filtrar por webhook"
        clearable
        outlined
        dense
        style="max-width: 300px"
        class="mr-3"
        @change="loadLogs"
      ></v-select>

      <!-- Filtro por estado -->
      <v-select
        v-model="statusFilter"
        :items="statusOptions"
        label="Estado"
        clearable
        outlined
        dense
        style="max-width: 200px"
        class="mr-3"
        @change="loadLogs"
      ></v-select>

      <!-- Botón refrescar -->
      <v-btn icon @click="loadLogs">
        <v-icon>mdi-refresh</v-icon>
      </v-btn>

      <!-- Botón cerrar -->
      <v-btn icon @click="$emit('close')">
        <v-icon>mdi-close</v-icon>
      </v-btn>
    </v-card-title>

    <v-divider></v-divider>

    <v-card-text>
      <!-- Tabla de logs -->
      <v-data-table
        :headers="headers"
        :items="logs"
        :loading="logsLoading"
        loading-text="Cargando logs..."
        no-data-text="No hay logs disponibles"
        class="elevation-1"
        :items-per-page="10"
        :footer-props="{
          'items-per-page-options': [10, 25, 50, 100]
        }"
      >
        <!-- Columna: Estado -->
        <template v-slot:item.status="{ item }">
          <v-chip
            small
            :color="getStatusColor(item.status)"
            :text-color="item.status === 'pending' ? 'black' : 'white'"
          >
            <v-icon small left>{{ getStatusIcon(item.status) }}</v-icon>
            {{ getStatusLabel(item.status) }}
          </v-chip>
        </template>

        <!-- Columna: Evento -->
        <template v-slot:item.event_name="{ item }">
          <div>
            <div class="font-weight-medium">{{ item.event_name }}</div>
            <div class="text-caption text--secondary">
              {{ item.entity_type }}
            </div>
          </div>
        </template>
        <!-- Columna: Response Status -->
        <template v-slot:item.response_status="{ item }">
          <v-chip
            v-if="item.response_status"
            x-small
            :color="getHttpStatusColor(item.response_status)"
            text-color="white"
          >
            {{ item.response_status }}
          </v-chip>
          <span v-else class="text--secondary">-</span>
        </template>

        <!-- Columna: Tiempo de ejecución -->
        <template v-slot:item.execution_time_ms="{ item }">
          <span v-if="item.execution_time_ms !== undefined">
            {{ item.execution_time_ms }}ms
          </span>
          <span v-else class="text--secondary">-</span>
        </template>

        <!-- Columna: Fecha -->
        <template v-slot:item.triggered_at="{ item }">
          <div class="text-caption">
            {{ formatDate(item.triggered_at) }}
          </div>
        </template>

        <!-- Columna: Acciones -->
        <template v-slot:item.actions="{ item }">
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                icon
                small
                v-bind="attrs"
                v-on="on"
                @click="viewLogDetail(item)"
              >
                <v-icon small>mdi-eye</v-icon>
              </v-btn>
            </template>
            <span>Ver detalles</span>
          </v-tooltip>
        </template>
      </v-data-table>
    </v-card-text>

    <!-- Diálogo de detalle del log -->
    <v-dialog v-model="detailDialog" max-width="1000" scrollable>
      <webhook-log-detail
        v-if="detailDialog && selectedLog"
        :log="selectedLog"
        @close="detailDialog = false"
      />
    </v-dialog>
  </v-card>
</template>

<script>
import { mapState } from "vuex";
import WebhookLogDetail from "./WebhookLogDetail.vue";

export default {
  name: "WebhookLogs",
  components: {
    WebhookLogDetail
  },
  props: {
    webhookId: {
      type: String,
      default: null
    }
  },
  data() {
    return {
      headers: [
        { text: "Estado", value: "status", sortable: true, width: "120px" },
        { text: "Evento", value: "event_name", sortable: true },
        { text: "Webhook", value: "webhook_config_id.name", sortable: false },
        {
          text: "HTTP Status",
          value: "response_status",
          sortable: true,
          align: "center"
        },
        {
          text: "Tiempo",
          value: "execution_time_ms",
          sortable: true,
          align: "right"
        },
        { text: "Fecha", value: "triggered_at", sortable: true },
        { text: "Acciones", value: "actions", sortable: false, align: "center" }
      ],
      selectedWebhookFilter: null,
      statusFilter: null,
      statusOptions: [
        { text: "Exitoso", value: "success" },
        { text: "Fallido", value: "failed" },
        { text: "Pendiente", value: "pending" }
      ],
      detailDialog: false,
      selectedLog: null
    };
  },
  computed: {
    ...mapState("webhooks", ["logs", "logsLoading", "webhooks"]),
    webhookOptions() {
      return [{ name: "Todos los webhooks", _id: null }, ...this.webhooks];
    }
  },
  mounted() {
    this.loadLogs();
  },
  methods: {
    async loadLogs() {
      try {
        const params = {};

        if (this.statusFilter) {
          params.status = this.statusFilter;
        }

        if (this.webhookId) {
          // Logs de un webhook específico
          await this.$store.dispatch("webhooks/fetchWebhookLogs", {
            webhookId: this.webhookId,
            params
          });
        } else if (this.selectedWebhookFilter) {
          // Logs de un webhook seleccionado en el filtro
          await this.$store.dispatch("webhooks/fetchWebhookLogs", {
            webhookId: this.selectedWebhookFilter,
            params
          });
        } else {
          // Todos los logs
          await this.$store.dispatch("webhooks/fetchAllLogs", params);
        }
      } catch (error) {
        this.$snotify.error("Error al cargar logs", "Error");
      }
    },
    viewLogDetail(log) {
      this.selectedLog = log;
      this.detailDialog = true;
    },
    getWebhookName(webhookId) {
      const webhook = this.webhooks.find(w => w._id === webhookId);
      return webhook?.name || "Webhook desconocido";
    },
    getStatusColor(status) {
      const colors = {
        success: "success",
        failed: "error",
        pending: "warning"
      };
      return colors[status] || "grey";
    },
    getStatusIcon(status) {
      const icons = {
        success: "mdi-check-circle",
        failed: "mdi-alert-circle",
        pending: "mdi-clock-outline"
      };
      return icons[status] || "mdi-help-circle";
    },
    getStatusLabel(status) {
      const labels = {
        success: "Exitoso",
        failed: "Fallido",
        pending: "Pendiente"
      };
      return labels[status] || status;
    },
    getHttpStatusColor(status) {
      if (status >= 200 && status < 300) return "success";
      if (status >= 300 && status < 400) return "info";
      if (status >= 400 && status < 500) return "warning";
      if (status >= 500) return "error";
      return "grey";
    },
    formatDate(date) {
      if (!date) return "-";
      const d = new Date(date);
      return d.toLocaleDateString("es-ES", {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit"
      });
    }
  }
};
</script>

<style scoped>
.v-data-table >>> .v-data-table__wrapper {
  max-height: calc(100vh - 400px);
}
</style>
